import { AfterViewInit, Component, ElementRef, EventEmitter, Input, Output, effect, inject } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { FormControl, Validators, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from "@angular/material/core";
import { DateFnsAdapter } from "@angular/material-date-fns-adapter";
import { format } from "date-fns";
import { enUS } from "date-fns/locale";
import { MessageService } from "../../../main";
import { AppSettingsService, SpinnerService } from "../../../core";
import { BucketItemService } from "../service/bucket-item.service";
import { catchError, map, throwError } from "rxjs";
import { HttpErrorResponse } from "@angular/common/http";
import { IUpdateStudyCompletionDateRequest } from "../models/UpdateStudyCompletionDate-request.model";
import { IBreadcrumbItem } from "../../../main/layout/tool-bar/model/breadcrumb.model";
import { BreadcrumbDataService } from "../../../main/layout/tool-bar/breadcrumb-service/breadcrumb-data.service";
import { Clipboard } from "@angular/cdk/clipboard";
import { CommonConsants } from "../constants/component-constants";
import { IUpdateTag, IUpdateTagResponse } from "../models/update-tag.model";
import { IUpdateStudyCompletionDateResponse } from "../models/update-completion-date-response.model";
import { JustificationDialogComponent } from "../../../shared/justification-dialog/justification-dialog.component";
import { MatDialog } from "@angular/material/dialog";
import { BucketItemsComponent } from "../bucket-items.component";
import { DataService } from "../service/data.service";
import { RowDataService } from "../service/row-data.service";
import { IBucketItem } from "../models/bucket-item.model";
import { ITags } from "../models/bucket-item.model";
import { TooltipDisableDirective } from "../../../shared/custom-directives/tooltip.directive";
import { MatTooltip } from "@angular/material/tooltip";
import { MatIconButton, MatButton } from "@angular/material/button";
import { MatRippleDirective } from "../../../shared/custom-directives/matRipple.directive";
import { FileMetaDataComponent } from "../file-meta-data/file-meta-data.component";
import { MatFormField, MatInput, MatSuffix, MatError } from "@angular/material/input";
import { MatDatepickerInput, MatDatepickerToggle, MatDatepickerToggleIcon, MatDatepicker } from "@angular/material/datepicker";
import { TagsComponent } from "../tags/tags.component";
import { PackageFolderTagsGeneralComponent } from "../package-folder-tags-general/package-folder-tags-general.component";

@Component({
    selector: "app-general-information",
    templateUrl: "./general-information.component.html",
    styleUrls: ["./general-information.component.scss"],
    providers: [
        {
            provide: DateAdapter,
            useClass: DateFnsAdapter,
        },
        {
            provide: MAT_DATE_LOCALE,
            useValue: enUS,
        },
        {
            provide: MAT_DATE_FORMATS,
            useValue: {
                parse: {
                    dateInput: [
                        "dd-MM-yyyy",
                        "dd MMM yyyy",
                        "dd/MM/yyyy",
                        "dd MM yyyy",
                        "yyyy MM dd",
                        "yyyy MMM dd",
                        "PP",
                        "P",
                    ],
                },
                display: {
                    dateInput: "dd MMM yyyy",
                    monthYearLabel: "MMM yyyy",
                    dateA11yLabel: "dd MMM yyyy",
                    monthYearA11yLabel: "MMM yyyy",
                },
            },
        },
    ],
    imports: [TooltipDisableDirective, MatTooltip, MatIconButton, MatRippleDirective, FileMetaDataComponent, MatFormField, MatInput, MatDatepickerInput, FormsModule, ReactiveFormsModule, MatDatepickerToggle, MatSuffix, MatDatepickerToggleIcon, MatDatepicker, MatError, MatButton, TagsComponent, PackageFolderTagsGeneralComponent]
})
export class GeneralInformationComponent implements AfterViewInit {
  private readonly bucketItemsService = inject(BucketItemService);
  private readonly rowDataService = inject(RowDataService);
  private readonly messageService = inject(MessageService);
  private readonly appSettingService = inject(AppSettingsService);
  private readonly spinnerService = inject(SpinnerService);
  private readonly breadcrumbDataService = inject(BreadcrumbDataService);
  private readonly dataService = inject(DataService);
  private readonly clipboard = inject(Clipboard);
  private readonly dialog = inject(MatDialog);
  private readonly el = inject(ElementRef);
  private readonly _breadcrumb = toSignal(this.breadcrumbDataService.selectedBreadcrumb$);
  private readonly _clickedRow = toSignal(this.rowDataService.getClickedRowData());
  private readonly _allSelected = toSignal(this.dataService.getAllSelectedData());

  @Output() bucketItem = new EventEmitter<boolean>();
  @Output() bucketItemId = new EventEmitter<string>();
  @Output() updateTagsFlag = new EventEmitter<boolean>();

  @Input() isFromSearch?: boolean = false;

  public checksum_fail_tooltip = CommonConsants.CHECKSUM_FAIL_TOOLTIP;
  public clickedBucketItem: Partial<IBucketItem> = {};
  public submitButtonClicked = false;
  public IsChecksumIconsHided: boolean = true;
  public placeHolder:string ="";
  public studyCompletionDate: FormControl = new FormControl("", [
    Validators.required,
  ]);
  // Set max date to today's UTC date (not local date) to prevent selecting future dates
  TodayMaxDate = (() => {
    const now = new Date();
    return new Date(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 23, 59, 59, 999);
  })();
  public requestData: IUpdateStudyCompletionDateRequest;
  public currentBreadcrumbItem: IBreadcrumbItem = null;
  public tags: Partial<ITags>[] = [];
  public updateTagsRequest: Partial<IUpdateTag> ={};
  public fromRequesterPackageFolderPage:boolean=false;
  public selectedFolderCount: number = 0;
  private readonly featureToken = "bucket-items";
  private readonly surfaceToken = "general-information";

  constructor() {
    effect(() => {
      const item = this._breadcrumb();
      if (item != null) this.currentBreadcrumbItem = item as IBreadcrumbItem;
    });
    effect(() => {
      const data = this._clickedRow();
      if (data && data !== null) {
        this.clickedBucketItem = data;
        this.tags = [];
        data.Tags.map((tag) => {
          this.tags.push({
            TagKey: tag.TagKey,
            TagValue: tag.TagValue,
            TagsDisplayOrder: tag.TagsDisplayOrder,
          });
        });
        if (
          this.clickedBucketItem &&
          this.clickedBucketItem.StudyCompletionDate !== null
        ) {
          this.studyCompletionDate = new FormControl(
            this.parseUtcDate(this.clickedBucketItem.StudyCompletionDate)
          );
        } else if (
          this.clickedBucketItem &&
          this.clickedBucketItem.StudyCompletionDate === null
        ) {
          this.studyCompletionDate = new FormControl(null);
        }
        if (this.isFromSearch) {
          if (this.clickedBucketItem.FolderType === 'FacilityPackage') {
            this.placeHolder = "Not Applicable";
          } else if (this.clickedBucketItem.StudyCompletionDate) {
            this.placeHolder = format(this.parseUtcDate(this.clickedBucketItem.StudyCompletionDate), 'dd MMM yyyy');
          } else {
            this.placeHolder = "";
          }
        } else {
          this.placeHolder = "Not Applicable";
        }
      }
    });
    effect(() => {
      const selectedItems = this._allSelected() ?? [];
      this.selectedFolderCount = selectedItems.length;
    });
    effect(() => {
      this.fromRequesterPackageFolderPage = localStorage.getItem('requesterPackageFolderComponent') === 'true';
    });
  }

  /** Parse an API date string as UTC and return a local-timezone Date object.
   * Appends 'Z' if no timezone designator is present, matching moment.utc().local() behaviour. */
  private parseUtcDate(dateStr: string): Date {
    return new Date(/Z$|[+-]\d{2}:?\d{2}$/.test(dateStr) ? dateStr : dateStr + 'Z');
  }

  public ngAfterViewInit(): void {
    this.clickedBucketItem.ArchivingEndDate =
    this.clickedBucketItem.ArchivingEndDate != null ? format(this.parseUtcDate(this.clickedBucketItem.ArchivingEndDate), 'dd MMM yyyy') : null;
    if(BucketItemsComponent.requestedAccess){
      this.el.nativeElement.scrollIntoView({behavior:'auto',block:'nearest'});
      BucketItemsComponent.requestedAccess=false;
    }
  }

  public updateTags(selectedTags: Partial<ITags>[]) {
    this.spinnerService.show(true);
    this.updateTagsRequest = {...this.updateTagsRequest,
      BucketType: this.currentBreadcrumbItem.BucketType,
      BUCode: this.currentBreadcrumbItem.BUCode,
      PackageId: this.clickedBucketItem.PackageId,
      Tags: [
        { BucketItemId: this.clickedBucketItem.Id, tags: [...selectedTags] },
      ],
    };
    this.bucketItemsService
      .UpdateTags(this.updateTagsRequest)
      .pipe(
        map((res: IUpdateTagResponse) => {
          this.spinnerService.show(false);
          if(res.JustificationRequired){
            this.dialog.open(JustificationDialogComponent,{
              width:"450px",
              disableClose:true
            }).afterClosed().subscribe(
              (response: {comment:string})=>{
                if(response && response.comment){
                  this.updateTagsRequest.Comment = response.comment;
                  //It will make the process continue after giving the justification
                  this.updateTags(selectedTags);
                }
              }
            );
          }else{
            this.updateTagsFlag.emit(true);
            if(res.SuccessfulMessage == CommonConsants.UPDATE_TAGS_PARTIAL_PERMISSION){
              this.messageService.warning(res.SuccessfulMessage,this.appSettingService.toastrDisplayTime);
            }
            else{
              this.messageService.success(res.SuccessfulMessage,this.appSettingService.toastrDisplayTime);
            }
            this.scrollToTop();
          }
        }),
        catchError((error: HttpErrorResponse) => {
          this.spinnerService.show(false);
          if(error.error.detail !== undefined){
            this.messageService.alert(error.error.detail,this.appSettingService.toastrDisplayTime);
          }
          else{
            this.messageService.alert("Not able to update Tag(s).Please try again later.",this.appSettingService.toastrDisplayTime);
          }
          return throwError(() => new Error(error.message));
        })
      )
      .subscribe();
  }

  public submitStudyCompletionDate() {
    this.studyCompletionDate.markAsTouched();
    if (
      this.studyCompletionDate.value === "" ||
      !this.studyCompletionDate.value || this.studyCompletionDate.invalid
    ) {
      this.studyCompletionDate.setErrors({ required: true });
    } else {
      this.spinnerService.show(true);
      const completionDate = format(this.studyCompletionDate.value as Date, 'dd MMM yyyy');

      this.requestData = {...this.requestData,
        BucketId: this.currentBreadcrumbItem.BucketId,
        BUCode: this.currentBreadcrumbItem.BUCode,
        BucketType: this.currentBreadcrumbItem.BucketType,
        BucketItemId: this.clickedBucketItem.Id,
        StudyCompletionDate: completionDate,
      };

      this.bucketItemsService
        .UpdateStudyCompletionDate(this.requestData)
        .pipe(
          map((res: Partial<IUpdateStudyCompletionDateResponse>) => {
            this.spinnerService.show(false);
            if(res.JustificationRequired){
              this.dialog.open(JustificationDialogComponent,{
                width:"450px",
                disableClose:true
              }).afterClosed().subscribe(
                (response: {comment:string})=>{
                  if(response && response.comment){
                    this.requestData.Comment = response.comment;
                    //It will make the process continue after giving the justification
                    this.submitStudyCompletionDate();
                  }
                }
              );
            }else{
              this.bucketItem.emit(res.UpdateSuccessful);
              this.messageService.success(
              `Study Completion Date updated successfully`,
              this.appSettingService.toastrDisplayTime
            );
            }
          }),
          catchError((error: HttpErrorResponse) => {
            this.studyCompletionDate.reset();

            this.spinnerService.show(false);
            if (error.error.detail != undefined) {
              this.messageService.alert(
                error.error.detail,
                this.appSettingService.toastrDisplayTime
              );
            } else {
              this.messageService.alert(
                "Study Completion Date update Failed",
                this.appSettingService.toastrDisplayTime
              );
            }
            return throwError(() => new Error(error.message));
          })
        )
        .subscribe();
    }
  }

  public copyToClipBoard() {
    this.clipboard.copy(this.clickedBucketItem.StreamHash);
    this.messageService.success(
      CommonConsants.COPY_CLIPBOARD_SUCCESS,
      this.appSettingService.toastrDisplayTime
    );
  }

  public hideChecksumIcons(action: boolean) {
    this.IsChecksumIconsHided = action;
  }

  public regenerateChecksum() {
    this.bucketItemsService
      .RegenerateChecksum({
        BucketItemId: this.clickedBucketItem.Id,
        PackageId: this.clickedBucketItem.PackageId,
      })
      .pipe(
        map(() => {
          if (this.clickedBucketItem.ChecksumStatus === "VerifiedAndMatching") {
            this.spinnerService.show(false);
            this.messageService.success(
              CommonConsants.CHECKSUM_MATCH,
              this.appSettingService.toastrDisplayTime
            );
          }
          else{
            this.messageService.alert(
              CommonConsants.CHECKSUM_MISMATCH,
              this.appSettingService.toastrDisplayTime
            );
          }
        }),
        catchError((error: HttpErrorResponse) => {
          this.spinnerService.show(false);
          if (error.error.detail === CommonConsants.FOLDER_OR_FILE_DOES_NOT_EXIST || error.error.detail === CommonConsants.CHECKSUM_MISMATCH) {
            this.bucketItemId.emit(this.clickedBucketItem.Id);
            this.messageService.alert(
              CommonConsants.CHECKSUM_MISMATCH,
              this.appSettingService.toastrDisplayTime
            );
          } else if (error.error.detail !== undefined) {
            this.messageService.alert(
              error.error.detail,
              this.appSettingService.toastrDisplayTime
            );
          } else {
            this.messageService.alert(
              CommonConsants.REGENERATE_CHECKSUM_FAILED,
              this.appSettingService.toastrDisplayTime
            );
          }
          return throwError(() => new Error(error.message));
        })
      )
      .subscribe();
  }

  
  public onPackageFolderTagsUpdated(updated: boolean): void {
    if (updated) {
      this.updateTagsFlag.emit(true);
    }
  }

  scrollToTop(){
    this.el.nativeElement.scrollIntoView({behavior:'smooth',block:'start'});
  }

  public buildControlId(element: string, state?: string): string {
    const tokens = [
      this.featureToken,
      this.surfaceToken,
      this.slugify(element),
    ];
    if (state) {
      tokens.push(this.slugify(state));
    }
    return tokens.join('-');
  }

  private slugify(value: string): string {
    return (value || 'na')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .replace(/-{2,}/g, '-');
  }
}
